import * as Sentry from '@sentry/browser';
import { routerRedux } from 'dva/router';
import { addLocaleData } from 'react-intl';
import queryString from 'query-string';
import en from 'react-intl/locale-data/en';
import de from 'react-intl/locale-data/de';
import { notification } from 'antd';
import pathToRegexp from 'path-to-regexp';
import ls from 'local-storage';
import messages from '../i18n';
import config from '../config';
import * as AR from '../services/app';
import * as CR from '../services/clients';
import { helpers } from '../utils';
import { defaultRecognitionNotice } from '../types';

addLocaleData([...en, ...de]);

export const ACTION_APP_GO_TO_ROUTE = 'app/goToRoute';
export const ACTION_APP_SHOW_NOTIFICATION = 'app/showNotifications';
export const ACTION_APP_ADD_NOTIFICATION = 'app/addNotification';
export const ACTION_APP_REFRESH_RECOGNITION_NOTICE = 'app/refreshRecognitionNotice';
export const ACTION_APP_DISMISS_RECOGNITION_NOTICE = 'app/dismissRecognitionNotice';

const defaultState = {
  messages,
  locale: 'en',
  user: {
    first_name: '',
    last_name: '',
    locale: '',
    email: '',
    avatar: '',
    display_datetime_format: 'DD.MM.YYYY HH:mm',
    display_timezone: 'Europe/Berlin',
    hubspot_permissions: [-1, -2],
    recognition_notice: defaultRecognitionNotice,
  },
  projects: [],
  firstVisit: true,
  token: window.localStorage.getItem('access_token'),
  auth: false,
  currentProjectId: 0, // deprecated, please use currentProject.id
  currentProject: {},
  roles: [],
  notifications: [],
  firstLogin: {
    email: '',
    token: '',
    validToken: null,
  },
  useCustomBranches: false,
  matomoProjectId: 0,
  header: {
    title: null,
    description: null,
  },
  // level of the recognition notice the user closed in this session (kept in memory only,
  // so the notice is shown again after the next login / page load)
  recognitionNoticeDismissedLevel: null,
};

export default {
  namespace: 'app',

  state: defaultState,

  reducers: {
    updateStore(state, { payload }) {
      return {
        ...state,
        ...payload,
      };
    },

    updateToken(state, { payload: { token } }) {
      window.localStorage.setItem('access_token', token);
      return {
        ...state,
        token,
      };
    },
  },

  subscriptions: {
    setup({ dispatch }) {
      const parsed = queryString.parseUrl(window.location.pathname + window.location.hash);
      const { email, token, locale } = parsed.query;
      const path = parsed.url.substr(0, parsed.url.length - 1);
      const recoverPassword = path === config['app.frontend_forget_password_url'];

      if (config['app.system.branch_type'] === 'CUSTOM') {
        dispatch({ type: 'updateStore', payload: { useCustomBranches: true } });
      }

      if (locale) {
        const payload = { locale };
        if (recoverPassword) {
          payload.firstVisit = false;
        }
        dispatch({
          type: 'updateStore',
          payload,
        });
      }

      if ((recoverPassword || path === config['app.frontend_change_password_url']) && token && email) {
        window.localStorage.setItem('access_token', false);
        dispatch({
          type: 'tokenValidator',
          payload: { email, token },
        });
      } else if (window.localStorage.getItem('access_token')) {
        dispatch({ type: 'ping' });
      } else {
        dispatch({ type: 'logout' });
      }
    },

    setupHistory({ dispatch, history }) {
      history.listen((location) => {
        dispatch({
          type: 'updateStore',
          payload: { location },
        });
      });
    },

  },
  effects: {
    * ping({ payload }, { call, put, select }) {
      try {
        const data = yield call(AR.pingRequest, payload);
        if (data.success) {
          yield put({
            type: 'authenticated',
            payload: {
              auth: true,
            },
          });

          const { location } = yield select(store => store.app);
          if (location.pathname === '/login/') {
            yield put(routerRedux.push('/'));
          } else if (location.pathname === '/logout/') {
            yield put({ type: 'logout' });
          }
        } else {
          yield put({ type: 'logout' });
        }
      } catch (e) {
        helpers.processException(e);
      }
    },

    * authenticated({ payload: { auth } }, { put }) {
      yield put({ type: 'updateStore', payload: { auth } });
      if (auth) {
        yield put({ type: 'start' });
      }
    },

    * start(options, { put }) {
      yield put.resolve({ type: 'loadCurrentUserData' });
      yield put.resolve({ type: 'loadRoles' });

      // load default project
      yield put.resolve({ type: 'selectProject', payload: { init: true } });
    },

    * selectProject({ payload = {} }, { select, put }) {
      const appStore = yield select(store => store.app);
      const { projects, location, user } = appStore;
      const defaultProjectId = helpers.getDefaultProjectId(projects);
      const urlParams = pathToRegexp('/:projectId/(.*)').exec(location.pathname);
      // const [, projectIdFromUrl, restOfUrl] = urlParams || [];
      const [, projectIdFromUrl] = urlParams || [];
      const pId = Number(payload.projectId || projectIdFromUrl);

      const projectFromUrl = projects.find(p => p.id === pId);
      const defaultProject = projects.find(p => p.id === defaultProjectId);

      if (!projectFromUrl && !defaultProject) {
        Sentry.captureMessage('No current project for user', {
          level: 'error',
          extra: {
            projects,
            location,
            user,
          },
        });
      }

      if (pId && projectFromUrl) {
        yield put({ type: 'updateStore', payload: { currentProjectId: pId, currentProject: projectFromUrl } });
        // yield put(routerRedux.replace(`/${pId}/${restOfUrl}`));
        // if not initial loading page
        if (!payload.init) {
          // navigates to visits grid to hide latin text on Home page
          yield put({
            type: 'goToRoute',
            payload: {
              routeName: 'LEAD_VISITS_LIST',
              projectId: pId,
            },
          });
        }

        return pId;
      } else if (defaultProjectId && (pId || location.pathname === '/')) {
        yield put({ type: 'updateStore', payload: { currentProjectId: defaultProjectId, currentProject: defaultProject } });
        // yield put(routerRedux.replace(`/${defaultProjectId}/`));

        // navigates to visits grid to hide latin text on Home page
        yield put({ type: 'goToRoute', payload: { routeName: 'LEAD_VISITS_LIST', projectId: defaultProjectId } });
        return defaultProjectId;
      } else {
        // not a number in url and no default project means there is no assigned projects to user
        yield put({ type: 'updateStore', payload: { currentProjectId: defaultProjectId, currentProject: defaultProject } });
        return defaultProjectId;
      }
    },

    // eslint-disable-next-line no-unused-vars
    * loadRoles({ payload = {} }, { call, put }) {
      try {
        const { success, data } = yield call(AR.rolesRequest);
        if (success) {
          yield put({ type: 'updateRoles', payload: { roles: data } });
        }
      } catch (e) {
        helpers.processException(e);
      }
    },

    * updateRoles({ payload: { roles } }, { put }) {
      yield put({ type: 'updateStore', payload: { roles } });
    },

    // eslint-disable-next-line no-unused-vars
    * loadCurrentUserData({ payload }, { put, call, select }) {
      try {
        const appStore = yield select(store => store.app);
        const { user } = appStore;
        let currentUserDataResponse = null;
        if (!user.id) {
          currentUserDataResponse = (yield call(AR.currentUserDataRequest)).body;
          currentUserDataResponse.hubspot_permissions = currentUserDataResponse.hubspot_permissions.map(i => i.project_id);
        }
        const currentUser = currentUserDataResponse || user;

        const { locale, client } = currentUser;
        let selectedClient = client;

        if (!client) {
          try {
            const lastSelectedClientId = ls.get('SELECTED_CLIENT_ID');
            if (lastSelectedClientId) {
              selectedClient = (yield call(CR.clientsItemRequest, { id: lastSelectedClientId })).body;
            } else {
              selectedClient = (yield call(CR.clientsDefaultRequest)).body;
            }
          } catch (e) {
            selectedClient = (yield call(CR.clientsDefaultRequest)).body;
          }
        }

        const defaultUiSettings = selectedClient ? (yield call(CR.clientsUiSettingsRequest, { id: selectedClient.id })).body : {};

        const isTwoFactorCorrect = !config['features.enforce_2fa'] || !!currentUser.two_factor_confirmed_at;
        let projectsResponse = {};
        let projects = currentUser.projects;

        if (currentUser.super_admin && isTwoFactorCorrect && selectedClient) {
          projectsResponse = yield call(AR.projectsRequest, { clientId: selectedClient.id });
          projects = projectsResponse.data;
        }

        const payloadResult = {
          locale,
          projects,
          selectedClient,
          defaultUiSettings,
          user: {
            ...currentUser,
          },
        };

        Sentry.configureScope((scope) => {
          scope.setUser({
            id: payloadResult.user.id,
            email: payloadResult.user.email,
            super_admin: payloadResult.user.super_admin,
            client_id: payloadResult.user.client_id,
          });
        });

        yield put({
          type: 'updateCurrentUserData',
          payload: payloadResult,
        });

        if (!isTwoFactorCorrect) {
          yield put({ type: 'goToRoute', payload: { routeName: 'PROFILE' } });
        }
      } catch (e) {
        helpers.processException(e);
        return Promise.reject();
      }
    },

    // eslint-disable-next-line no-unused-vars
    * getDefaultSettings({ payload = {} }, { call, select, put }) {
      const { selectedClient } = yield select(store => store.app);
      const defaultUiSettings = (yield call(CR.clientsUiSettingsRequest, { id: selectedClient.id })).body;

      yield put({
        type: 'updateCurrentUserData',
        payload: {
          defaultUiSettings,
        },
      });
    },

    * selectClient({ payload }, { put }) {
      const clientId = payload.clientId;
      ls.set('SELECTED_CLIENT_ID', clientId);
      yield put.resolve({ type: 'loadCurrentUserData' });

      yield put.resolve({ type: 'selectProject' });
    },

    * updateCurrentUserData({ payload }, { put }) {
      yield put({ type: 'updateStore', payload });
    },

    * waitUntilAppDataLoaded(p, { select, take }) {
      const { currentProjectId } = yield select(store => store.app);
      if (!currentProjectId) {
        // if no project selected yet, then wait until selected and proceed
        yield take('start/@@end');
      }
    },

    * refreshRecognitionNotice(options, { call, put, select }) {
      try {
        const recognitionNotice = (yield call(AR.currentUserRecognitionNoticeRequest)).body;
        const { user } = yield select(store => store.app);
        yield put({
          type: 'updateStore',
          payload: { user: { ...user, recognition_notice: recognitionNotice } },
        });
        return recognitionNotice;
      } catch (e) {
        // background re-check only, keep the last known notice
        return null;
      }
    },

    * dismissRecognitionNotice(options, { put, select }) {
      const { user } = yield select(store => store.app);
      const { level } = user.recognition_notice || {};
      yield put({ type: 'updateStore', payload: { recognitionNoticeDismissedLevel: level } });
      yield put({ type: 'refreshRecognitionNotice' });
    },

    // eslint-disable-next-line no-unused-vars
    * showNotifications({ payload }, { select, put }) {
      const { notifications } = yield select(store => store.app);
      notifications.forEach((item) => {
        const { type } = item;
        notification[type](item);
      });

      if (notifications.length) {
        yield put({ type: 'clearAllNotifications' });
      }
    },

    * addNotification({ payload }, { select, put }) {
      const { locale, notifications } = yield select(store => store.app);
      yield put({
        type: 'updateStore',
        payload: {
          notifications: [...notifications, {
            ...payload,
            message: messages[locale][payload.message],
            description: messages[locale][payload.description],
          }],
        },
      });
    },

    * addAndShowNotification({ payload }, { put }) {
      yield put({ type: 'addNotification', payload });
      yield put({ type: 'showNotifications' });
    },

    // eslint-disable-next-line no-unused-vars
    * clearAllNotifications({ payload }, { put }) {
      yield put({ type: 'updateStore', payload: { notifications: [] } });
    },

    * goToRoute({ payload }, { put }) {
      const { routeName, method = 'push', ...params } = payload;
      const returnPath = helpers.compileUrl(routeName, params);
      yield put(routerRedux[method](returnPath));
    },

    // eslint-disable-next-line no-unused-vars
    * logout({ payload }, { put }) {
      if (window.localStorage.getItem('access_token')) {
        helpers.forceLogout();
      } else {
        yield put({
          type: 'updateStore',
          payload: {
            ...defaultState,
          },
        });
        yield put({ type: 'goToRoute', payload: { routeName: 'LOGIN' } });
      }

      return Promise.reject();
    },

    * forgotPassword({ payload }, { call }) {
      try {
        return yield call(AR.forgotPasswordRequest, payload);
      } catch (e) {
        return Promise.reject();
      }
    },

    * tokenValidator({ payload }, { put, call }) {
      const { token, email } = payload;
      try {
        const data = yield call(AR.tokenValidationRequest, payload, 'tokenRequest');
        if (data.success) {
          yield put({ type: 'updateStore', payload: { firstLogin: { validToken: true, token, email } } });
          yield put({ type: 'goToRoute', payload: { routeName: 'FIRST_LOGIN', token, email } });
        }
      } catch (e) {
        yield put({
          type: 'updateStore',
          payload: { firstLogin: { validToken: false } },
        });
        yield put({ type: 'goToRoute', payload: { routeName: 'FIRST_LOGIN' } });
      }
    },

    * changePassword({ payload }, { call }) {
      return yield call(AR.tokenValidationRequest, payload);
    },
  },
};



// WEBPACK FOOTER //
// ./src/models/app.js